import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { getEntityAssets } from 'apis/asset-repository-service/api';
import { Asset, AssetScope, AssetSource, Image } from 'apis/asset-repository-service/types';

import { useScopeContext } from '../../../contexts/ScopeContext';

const LISTING_MANAGER_IMAGE_GALLERY_QUERY_KEY = 'listing-manager-images-gallery';

type Props = {
  size?: number;
  source?: AssetSource['type'];
  enabled?: boolean;
};

const isAssetImage = (asset: Asset): asset is Image => asset.contentType.startsWith('image/'); // filter out non-image assets

const isAssetScope = (scope: string): scope is AssetScope['type'] =>
  ['venue', 'merchant', 'brand', 'country', 'unspecified'].includes(scope);

export const useListingManagerImageGallery = ({ size = 12, source, enabled = true }: Props) => {
  const scope = useScopeContext();
  const queryKey = useMemo(
    () => [LISTING_MANAGER_IMAGE_GALLERY_QUERY_KEY, size, scope.type, scope.id, source],
    [size, scope, source],
  );

  const [updatingImagesCount, setUpdatingImagesCount] = useState<number>(0);

  const { data, ...infiniteQuery } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam: cursor }) =>
      getEntityAssets({
        scope: isAssetScope(scope.type) ? scope.type : 'unspecified',
        scopeId: scope.id,
        source,
        size,
        cursor,
      }).then(response => ({
        paged: response.paged,
        content: response.content?.filter(isAssetImage) ?? [],
      })),
    getNextPageParam: lastPage => lastPage.content[lastPage.content?.length - 1]?.id,
    refetchOnWindowFocus: false,
    staleTime: 60 * 60 * 1000, // 1 hour
    retry: false,
    enabled,
  });

  const imageAssets = data?.pages.flatMap(page => page.content) ?? [];

  const updateImageAssets = (upcommingImagesCount?: number) => {
    // refetch after a delay because new images are not immediately available
    setUpdatingImagesCount(upcommingImagesCount ?? 0);
    setTimeout(() => {
      infiniteQuery.refetch();
      setUpdatingImagesCount(0);
    }, 2000);
  };

  return {
    ...infiniteQuery,
    imageAssets,
    updateImageAssets,
    updatingImagesCount,
  };
};

export const useUpdateListingManagerImageGallery = () => {
  const queryClient = useQueryClient();

  const refetchAllImageGalleryQueries = () =>
    queryClient.refetchQueries({
      queryKey: [LISTING_MANAGER_IMAGE_GALLERY_QUERY_KEY],
      exact: false,
    });

  const updateImageGalleryForNewImages = () =>
    new Promise(resolve => {
      // refetch after a delay because new images are not immediately available
      setTimeout(() => refetchAllImageGalleryQueries().then(resolve), 2000);
    });

  return {
    updateImageGalleryForNewImages,
  };
};
